import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Navigation, Clock, MapPin } from "lucide-react";
import { Card, CardContent, Badge } from "@tabipla/ui";
import type { TripResponseDto } from "@tabipla/shared";
import { getTripCover } from "@/utils/trip-cover";

interface TodaySchedulePanelProps {
  trips: TripResponseDto[];
}

/**
 * Calculate if a trip is currently in "traveling" mode
 * (today is between start and end date, inclusive)
 */
function isTripTraveling(trip: TripResponseDto): boolean {
  if (!trip.startDate || !trip.endDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(trip.startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(trip.endDate);
  end.setHours(23, 59, 59, 999);

  return today >= start && today <= end;
}

/**
 * Calculate which day of the trip it is today (1-indexed)
 */
function getTripDayNumber(trip: TripResponseDto): number {
  if (!trip.startDate) return 1;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(trip.startDate);
  start.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - start.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays + 1;
}

export function TodaySchedulePanel({ trips }: TodaySchedulePanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Find a trip that is currently in "traveling" mode
  const travelingTrip = useMemo(() => {
    return trips.find((trip) => isTripTraveling(trip)) ?? null;
  }, [trips]);

  if (!travelingTrip) {
    return null;
  }

  const cover = getTripCover(travelingTrip.destination, travelingTrip.interests);
  const coverEmoji = travelingTrip.coverEmoji ?? cover.emoji;
  const dayNumber = getTripDayNumber(travelingTrip);

  return (
    <Card className="overflow-hidden border-green-200 bg-gradient-to-br from-green-50 to-green-100 dark:border-green-800 dark:from-green-950 dark:to-green-900">
      <CardContent className="p-0">
        <button
          onClick={() => navigate(`/trips/${travelingTrip.id}`)}
          className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-green-100/50 dark:hover:bg-green-900/50"
        >
          {/* Trip Emoji */}
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/80 text-3xl shadow-sm dark:bg-gray-800/80">
            {coverEmoji}
          </div>

          {/* Trip Info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Badge className="animate-pulse bg-green-500 text-white">
                <Navigation className="mr-1 h-3 w-3" />
                {t("trips.mode.onTrip")}
              </Badge>
              <span className="text-sm font-medium text-green-700 dark:text-green-300">
                {t("trips.dayNumber", { day: dayNumber })}
              </span>
            </div>
            <h3 className="mt-1 truncate text-lg font-bold text-green-900 dark:text-green-100">
              {travelingTrip.title}
            </h3>
            <div className="mt-0.5 flex items-center gap-3 text-sm text-green-700 dark:text-green-300">
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {travelingTrip.destination}
              </span>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex shrink-0 flex-col items-center gap-1">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500 text-white">
              <Clock className="h-5 w-5" />
            </div>
            <span className="text-xs font-medium text-green-600 dark:text-green-400">
              {t("home.todaySchedule.viewSchedule")}
            </span>
          </div>
        </button>
      </CardContent>
    </Card>
  );
}
